/**
 * useTier -- A9-9 Tier-awareness hook.
 *
 * Fetches current tier + canonical TIER_FEATURES + PRICING.
 * Caches result; hasFeature() check is synchronous after initial fetch.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/apiClient';

const STATUS_TTL_MS = 5 * 60 * 1000; // 5 min
let _cachedStatus = null;
let _cachedStatusAt = 0;
let _cachedTiersData = null;

export default function useTier() {
  const [status, setStatus] = useState(_cachedStatus);
  const [tiersData, setTiersData] = useState(_cachedTiersData);
  const [isLoading, setIsLoading] = useState(!_cachedStatus || !_cachedTiersData);
  const aborted = useRef(false);

  const fetchStatus = useCallback(async () => {
    const now = Date.now();
    if (_cachedStatus && (now - _cachedStatusAt) < STATUS_TTL_MS) {
      setStatus(_cachedStatus);
      return _cachedStatus;
    }
    try {
      const res = await apiClient.get('/api/v1/aif/billing/status');
      const data = res?.data ?? res;
      _cachedStatus = data;
      _cachedStatusAt = now;
      if (!aborted.current) setStatus(data);
      return data;
    } catch {
      if (!aborted.current) setStatus(null);
      return null;
    }
  }, []);

  const fetchTiers = useCallback(async () => {
    if (_cachedTiersData) {
      setTiersData(_cachedTiersData);
      return _cachedTiersData;
    }
    try {
      const res = await apiClient.get('/api/v1/aif/billing/tiers');
      const data = res?.data ?? res;
      _cachedTiersData = data;
      if (!aborted.current) setTiersData(data);
      return data;
    } catch {
      if (!aborted.current) setTiersData(null);
      return null;
    }
  }, []);

  useEffect(() => {
    aborted.current = false;
    Promise.all([fetchStatus(), fetchTiers()]).finally(() => {
      if (!aborted.current) setIsLoading(false);
    });
    return () => { aborted.current = true; };
  }, [fetchStatus, fetchTiers]);

  const refetch = useCallback(async () => {
    _cachedStatus = null;
    _cachedStatusAt = 0;
    setIsLoading(true);
    const data = await fetchStatus();
    setIsLoading(false);
    return data;
  }, [fetchStatus]);

  const hasFeature = useCallback((featureName) => {
    if (!status || !tiersData) return false;
    const tier = status.subscription_tier || 'free';
    const tierFeatures = tiersData?.tiers?.[tier]?.features;
    if (!tierFeatures) return false;
    const value = tierFeatures[featureName];
    return value === true || (typeof value === 'number' && value > 0);
  }, [status, tiersData]);

  return {
    tier: status?.subscription_tier || null,
    isActive: status?.stripe_subscription_status === 'active'
              || status?.stripe_subscription_status === 'trialing'
              || (status?.subscription_tier === 'free' || !status?.subscription_tier),
    currentPeriodEnd: status?.current_period_end || null,
    pricing: tiersData?.pricing || null,
    features: tiersData?.tiers || null,
    isLoading,
    hasFeature,
    refetch,
  };
}

// Test-only: reset cache
export const __testHooks = {
  resetCache: () => {
    _cachedStatus = null;
    _cachedStatusAt = 0;
    _cachedTiersData = null;
  },
};
